// Shared utilities for onboarding add/edit controllers.
// Centralizes FormData parsing, VAT restructuring, CSRF handling,
// and the POST/PATCH + reload pattern used by both controllers.

/**
 * Parse a FormData object into a plain JS object.
 * Handles:
 *   - Simple fields:  key → data[key] = value
 *   - Array fields:   key[] → data[key] = [value, ...]
 *   - Nested fields:  key[subkey] → data[key] = { subkey: value }
 *
 * @param {FormData} formData
 * @param {string[]} excludeKeys - Keys to skip (e.g. ["index"])
 * @returns {Object}
 */
export function parseFormData(formData, excludeKeys = []) {
  const data = {};
  for (const [key, value] of formData.entries()) {
    if (excludeKeys.includes(key)) continue;

    if (key.endsWith('[]')) {
      const cleanKey = key.slice(0, -2);
      if (!data[cleanKey]) data[cleanKey] = [];
      data[cleanKey].push(value);
    } else if (key.includes('[')) {
      const match = key.match(/^(\w+)\[(\w+)\]$/);
      if (match) {
        const [, parent, child] = match;
        if (!data[parent]) data[parent] = {};
        data[parent][child] = value;
      }
    } else {
      data[key] = value;
    }
  }
  return data;
}

/**
 * Determine if a VAT rate is active based on a flag value and amount.
 * Uses robust checking that handles string booleans from form data.
 *
 * @param {*} flagValue  - "1", "0", "true", "false", true, false, or undefined
 * @param {*} amountValue - Numeric or string amount
 * @returns {boolean}
 */
function isVatActive(flagValue, amountValue) {
  if (flagValue === '1' || flagValue === 1 || flagValue === true || flagValue === 'true')
    return true;
  if (flagValue === '0' || flagValue === 0 || flagValue === false || flagValue === 'false')
    return false;
  return parseFloat(amountValue || 0) > 0;
}

/**
 * Restructure flat VAT fields in a data object into a nested vat_distribution.
 * Moves amount_vat_* and vat_*_active fields into data.vat_distribution,
 * then deletes the flat fields from the root.
 *
 * @param {Object} data - Mutable data object (modified in place)
 * @returns {Object} The same data object, with vat_distribution added
 */
export function restructureVatData(data) {
  data.vat_distribution = {
    vat_20_active: isVatActive(data.vat_20_active, data.amount_vat_20),
    amount_vat_20: parseFloat(data.amount_vat_20) || null,
    vat_10_active: isVatActive(data.vat_10_active, data.amount_vat_10),
    amount_vat_10: parseFloat(data.amount_vat_10) || null,
    vat_5_5_active: isVatActive(data.vat_5_5_active, data.amount_vat_5_5),
    amount_vat_5_5: parseFloat(data.amount_vat_5_5) || null,
  };

  delete data.amount_vat_20;
  delete data.amount_vat_10;
  delete data.amount_vat_5_5;
  delete data.vat_20_active;
  delete data.vat_10_active;
  delete data.vat_5_5_active;
  delete data.base_amount_ttc;
  delete data.amount_vat_0;

  return data;
}

/**
 * Get the CSRF token from the page meta tag.
 * @returns {string}
 */
export function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content || '';
}

/**
 * POST or PATCH onboarding data, then reload on success or alert on error.
 *
 * @param {string} url     - The endpoint URL
 * @param {string} method  - HTTP method ("POST" or "PATCH")
 * @param {Object} payload - JSON-serializable body
 * @returns {Promise<Object>} The parsed response
 */
export async function postOnboardingData(url, method, payload) {
  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  if (result.success) {
    window.location.reload();
  } else {
    alert(`Erreur : ${result.error}`);
  }

  return result;
}
